import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { WorkflowNodeInput } from '@cotera/api';
import { Text } from '@cotera/client/app/components/ui';
import { cn } from '@cotera/client/v0/lib/utils';
import { useToolSchemaLookup } from '@cotera/client/app/hooks/use-tool-schema';
import { useToolCallUpstreamSamples } from '../../../hooks/use-tool-call-upstream-samples';
import { asRecord, readString } from '../../utils';
import { jsonTemplateOfSchema, listStructFields } from './tool-input-schema';
import { readParamBindings } from './json-pointer-suggestions';
import { toolInputRefIssues } from './tool-input-ref-issues';
import { missingRequiredInputs, ToolInputsEditor } from './tool-inputs-editor';
import { TOOL_INPUT_JSON_CLASS } from './tool-input-styles';
import type { WorkflowNodeEditorProps } from './types';

type ParamValues = Record<string, unknown>;
type ParamBindings = Record<string, string>;

export const ToolCallNodeEditor: React.FC<WorkflowNodeEditorProps> = ({
  node,
  onChange,
}) => {
  const lookup = useToolSchemaLookup();
  const data = asRecord(node.data);
  const key = readString(data, 'key');
  const name = readString(data, 'name');

  const toolSchema = useMemo(
    () => (key !== undefined ? lookup(key) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key]
  );

  const fields = useMemo(
    () =>
      toolSchema === null
        ? []
        : listStructFields(
            toolSchema.inputSchema,
            toolSchema.inputFieldDescriptions
          ),
    [toolSchema]
  );

  // The initial params value. Struct tools always edit a record. Non-struct
  // tools (rare) keep whatever JSON shape was saved — array/primitive/object —
  // and, when nothing is saved, seed the schema template so the JSON editor
  // shows and persists the same value.
  const initialParams = useMemo<unknown>(() => {
    const raw = data?.['params'];
    if (fields.length > 0) {
      return asRecord(raw) ?? {};
    }
    if (raw !== undefined) {
      return raw;
    }
    return toolSchema === null
      ? {}
      : jsonTemplateOfSchema(toolSchema.inputSchema);
  }, [data, toolSchema, fields]);
  const initialBindings = useMemo<ParamBindings>(
    () => readParamBindings(data),
    [data]
  );

  const [params, setParams] = useState<unknown>(initialParams);
  const [bindings, setBindings] = useState<ParamBindings>(initialBindings);
  const [jsonText, setJsonText] = useState(() =>
    JSON.stringify(initialParams, null, 2)
  );
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Re-seed synchronously when the drawer switches to a different node, so the
  // token editors (keyed on node.id below) remount with the right values.
  const [seededNodeId, setSeededNodeId] = useState(node.id);
  if (seededNodeId !== node.id) {
    setSeededNodeId(node.id);
    setParams(initialParams);
    setBindings(initialBindings);
    setJsonText(JSON.stringify(initialParams, null, 2));
    setJsonError(null);
  }

  const { upstreamSamples } = useToolCallUpstreamSamples(node.id);

  /**
   * Refs on this node that read nothing off the steps before it.
   *
   * Computed here rather than inside the inputs editor because it has to do two
   * things: show the message next to the value that holds the bad ref, and stop
   * the node committing. A node saved with one of these looks configured, runs,
   * and writes a blank — which is what a workflow updating "the row with id
   * `{{__trigger__/rowKey}}`" did every time it fired.
   */
  const refIssues = useMemo(
    () =>
      toolInputRefIssues({
        params: asRecord(params) ?? {},
        bindings,
        upstreamSamples,
      }),
    [params, bindings, upstreamSamples]
  );
  const hasRefIssues = refIssues.length > 0;

  const buildNode = useCallback(
    (
      nextParams: unknown,
      nextBindings: ParamBindings
    ): WorkflowNodeInput | null => {
      if (key === undefined) {
        return null;
      }
      // Block save while required inputs are empty (drawer disables Save on null).
      if (
        missingRequiredInputs(fields, asRecord(nextParams) ?? {}, nextBindings)
          .length > 0
      ) {
        return null;
      }
      // Same for a ref that resolves to nothing: this editor autosaves, so
      // letting it through is saving it.
      if (hasRefIssues) {
        return null;
      }
      const paramBindings =
        Object.keys(nextBindings).length > 0 ? nextBindings : undefined;
      return {
        type: 'tool-call',
        data: {
          key,
          ...(name !== undefined && { name }),
          params: nextParams ?? {},
          ...(paramBindings !== undefined && { paramBindings }),
        },
      };
    },
    [key, name, fields, hasRefIssues]
  );

  // Report the current value so the drawer can save without an explicit edit,
  // and whenever the inputs change.
  useEffect(() => {
    onChange(buildNode(params, bindings));
  }, [params, bindings, buildNode, onChange]);

  const handleEditorChange = useCallback(
    (next: { params: ParamValues; bindings: ParamBindings }) => {
      setParams(next.params);
      setBindings(next.bindings);
    },
    []
  );

  if (key === undefined) {
    return (
      <Text.Caption className="text-muted-foreground block">
        Tool not set.
      </Text.Caption>
    );
  }
  if (toolSchema === null) {
    return (
      <Text.Caption className="text-muted-foreground block">
        Couldn&apos;t load the schema for {key}.
      </Text.Caption>
    );
  }

  // Tools whose top-level input isn't a struct (rare) fall back to raw JSON.
  if (fields.length === 0) {
    return (
      <div className="flex flex-col gap-2">
        <Text.Caption className="text-muted-foreground block">
          Edit this tool&apos;s parameters as JSON.
        </Text.Caption>
        <textarea
          className={cn(TOOL_INPUT_JSON_CLASS, 'min-h-[220px]')}
          value={jsonText}
          spellCheck={false}
          onChange={(event) => {
            const text = event.target.value;
            setJsonText(text);
            try {
              // Preserve the parsed shape (object / array / primitive) — non-struct
              // tools legitimately take non-object inputs.
              const parsed: unknown = JSON.parse(text);
              setJsonError(null);
              setParams(parsed);
            } catch {
              setJsonError('Invalid JSON');
            }
          }}
        />
        {jsonError !== null ? (
          <Text.Caption className="text-destructive block">
            {jsonError}
          </Text.Caption>
        ) : null}
      </div>
    );
  }

  return (
    <ToolInputsEditor
      key={node.id}
      fields={fields}
      params={asRecord(params) ?? {}}
      bindings={bindings}
      upstreamSamples={upstreamSamples}
      refIssues={refIssues}
      onChange={handleEditorChange}
      toolKey={key}
    />
  );
};
